import React, { useCallback, useState } from 'react';
import { View, ScrollView, Pressable, Text } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import styled from 'styled-components/native';

import DataState from 'app/src/components/DataState';
import Modal from 'app/src/elements/Modal';
import * as colors from 'app/src/styles/colors';
import { dollars } from 'app/src/utils/stringHelpers';

import {
  SaleAssetContainer,
  BorderedContainer,
  Title,
  Button,
  goingColor,
} from './elements';
import {
  BIDDING_SALE_ASSETS,
  BIDDING_PASS,
  REACTIVATE_BIDDING,
} from './queries';
import BidGroup from './BidGroup';
import BidGroupChoose from './BidGroupChoose';
import AnnouncementInput from '../Sales/Announcements/AnnouncementInput';
import AnnouncementIcon from '../Sales/Announcements/AnnouncementIcon';

const Container = styled(BorderedContainer)`
  height: 100%;
  flex: 1;
`;

const List = styled(ScrollView)`
`;

const Thumbnail = styled(View)`
  width: 180px;
  margin-right: 10px;
  align-items: center;
  text-align: center;
`;

const TopButtons = styled(View)`
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 10px;
  border-bottom-width: 1px;
  border-bottom-style: solid;
`;

const TopButton = styled(Pressable)`
`;

const TopLabel = styled(Title)`
  font-size: 20px;
  font-weight: 600;
`;

const ButtonsContainer = styled(View)`
  maxWidth: 200px;
  flex-direction: column;
  justify-content: space-evenly;
`;

const GoingText = styled(Text)`
  font-weight: 900;
  color: darkred;
`;

const StatusText = styled(Text)`
  font-weight: 600;
`;

const BUTTON_STYLE = {
  width: '100%',
  paddingTop: 1,
  paddingBottom: 1,
  maxHeight: 36,
};

export const SaleAsset = props => {
  const {
    saleAsset,
    active,
    updateSale,
    updateBiddingSaleAsset,
    setShowGroupModal,
    setShowBlastModal,
    setSaleAssetAnnouncements,
    setAnnouncementAssetDetails
  } = props;

  const { asset } = saleAsset;
  const [biddingPass] = useMutation(BIDDING_PASS);
  const [reactivateBidding] = useMutation(REACTIVATE_BIDDING);

  const confirmReactivate = () => {
    if (window.confirm('Really return this asset to active bidding?')) {
      reactivateBidding({ variables: { id: saleAsset.id } });
    }
  };

  const highBid = saleAsset.highBid && parseInt(saleAsset.highBid);

  return (
    <SaleAssetContainer
      active={active}
      groupPosition={saleAsset.groupPosition}>
      <Thumbnail>
        {asset.primaryPhotoUrl && (
          <img
            src={asset.primaryPhotoUrl}
            alt={asset.name}
            height="100"
            style={{ maxWidth: '100%' }}
          />
        )}
      </Thumbnail>
      <View style={{flex: 1}}>
        <View style={{display: 'flex', flexDirection: 'row'}}>
          <Text>{saleAsset.lotNumber}</Text>
          <AnnouncementIcon
            saleAsset={saleAsset}
            setSaleAssetAnnouncements={setSaleAssetAnnouncements}
            setAnnouncementAssetDetails={setAnnouncementAssetDetails}
          />
        </View>
        {'Passed' === saleAsset.biddingStatus && (
          <StatusText>PASSED</StatusText>
        )}
        {'Sold' === saleAsset.biddingStatus && (
          <StatusText>
            {saleAsset.pendingSellerConfirmation ?
              'Pending Seller Confirmation' : 'SOLD'
            }
            {' - '}{dollars(saleAsset.biddingSoldAmount)}
            {' - '}{saleAsset.biddingSoldBuyerDetails}
          </StatusText>
        )}
        {goingColor(saleAsset) && (
          <GoingText style={{ color: goingColor(saleAsset) }}>
            {saleAsset.biddingStatus}
          </GoingText>
        )}
        <Text>{asset.name}</Text>
        <Text>{asset.vin}</Text>
        <Text>{asset.cityState?.toUpperCase()}</Text>
        {active && !!highBid && (
          <StatusText>CB: {dollars(highBid)}</StatusText>
        )}
      </View>
      {updateBiddingSaleAsset && (
        <ButtonsContainer>
          {'Sold' !== saleAsset.biddingStatus && !active && (
            <Button
              title="Active"
              style={BUTTON_STYLE}
              color="cyan"
              labelStyle={{color: 'black'}}
              onPress={() => updateBiddingSaleAsset(saleAsset)}
            />
          )}
          {'Pending' === saleAsset.biddingStatus && (
            <Button
              title="Pass"
              style={BUTTON_STYLE}
              color={colors.elRed}
              onPress={
                () => biddingPass({ variables: { saleAssetId: saleAsset.id }})
              }
            />
          )}
          {'Pending' === saleAsset.biddingStatus && !active && (
            <Button
              title="Choice"
              style={BUTTON_STYLE}
              color="blue"
              onPress={() => setShowGroupModal(saleAsset)}
            />
          )}
          {'Sold' === saleAsset.biddingStatus && !active && (
            <Button
              title="Re-Activate"
              style={BUTTON_STYLE}
              color="cyan"
              labelStyle={{color: 'black'}}
              onPress={confirmReactivate}
            />
          )}
          {'Pending' === saleAsset.biddingStatus && (
            <Button
              title="Blast"
              style={BUTTON_STYLE}
              color="violet"
              onPress={() => {
                setAnnouncementAssetDetails(
                  `${saleAsset.lotNumber} | ${saleAsset.asset.name}`
                );
                setShowBlastModal(saleAsset)
              }}
            />
          )}
        </ButtonsContainer>
      )}
    </SaleAssetContainer>
  );
};

export const isActive = (sale, saleAsset) => (
  saleAsset.id === sale.biddingSaleAsset?.id
);

const SaleAssets = ({ sale, updateSale }) => {
  const [hideSold, setHideSold] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showBlastModal, setShowBlastModal] = useState(false);
  const [saleAssetAnnouncements, setSaleAssetAnnouncements] = useState(false);
  const [announcementAssetDetails, setAnnouncementAssetDetails] = useState(false);

  const { data } = useQuery(BIDDING_SALE_ASSETS, {
    variables: { saleId: sale.id }
  });

  const activeRef = useCallback(element => {
    if (element) {
      element.scrollIntoView();
    }
  }, []);

  if (!data) { return <DataState.Loading />; }

  const updateBiddingSaleAsset = saleAsset => {
    if ('next' === saleAsset) {
      if (sale.biddingSaleAsset) {
        let currentIndex = data.saleAssets.findIndex(item => (
          item.id === sale.biddingSaleAsset.id
        ));

        do {
          currentIndex = currentIndex + 1;
          saleAsset = data.saleAssets[currentIndex];
        } while ('Passed' === saleAsset.biddingStatus);
      } else {
        saleAsset = data.saleAssets[0];
      }
    };

    updateSale({
      variables: {
        id: sale.id,
        biddingSaleAssetId: (saleAsset?.id || null),
      }
    });
  };

  let saleAssets = hideSold ?
    data.saleAssets.filter(saleAsset => 'Sold' !== saleAsset.biddingStatus) :
    data.saleAssets;

  const groups = {};

  saleAssets.forEach(saleAsset => {
    if (saleAsset.pendingBidGroupId) {
      if (groups[saleAsset.pendingBidGroupId]) {
        groups[saleAsset.pendingBidGroupId].push(saleAsset.id);
      } else {
        groups[saleAsset.pendingBidGroupId] = [saleAsset.id];
      }
    }
  });

  saleAssets = saleAssets.map(saleAsset => {
    const group = groups[saleAsset.pendingBidGroupId];
    saleAsset = {...saleAsset };

    if (group) {
      const indexInGroup =
        group.indexOf(saleAsset.id);

      if (0 === indexInGroup) {
        saleAsset.groupPosition = 'first';
      } else if (indexInGroup === group.length - 1) {
        saleAsset.groupPosition = 'last';
      } else {
        saleAsset.groupPosition = 'middle';
      }
    }

    return saleAsset;
  });

  return (
    <Container>
      {updateSale && (
        <TopButtons>
          <TopButton onPress={() => updateBiddingSaleAsset('next')}>
            <TopLabel>Make Next Lot Active</TopLabel>
          </TopButton>
          <TopButton onPress={() => setHideSold(!hideSold)}>
            <TopLabel>
              {hideSold ?
                `Show ${sale.lotsSoldCount} Sold` :
                `Hide ${sale.lotsSoldCount} Sold`
              }
            </TopLabel>
          </TopButton>
        </TopButtons>
      )}

      <List>
        {saleAssets.map(saleAsset => (
          <React.Fragment key={saleAsset.id}>
            <div ref={isActive(sale, saleAsset) ?  activeRef : null}>
            </div>
            <SaleAsset
              saleAsset={saleAsset}
              active={isActive(sale, saleAsset)}
              updateBiddingSaleAsset={updateSale && updateBiddingSaleAsset}
              setShowGroupModal={setShowGroupModal}
              setShowBlastModal={setShowBlastModal}
              setSaleAssetAnnouncements={setSaleAssetAnnouncements}
              setAnnouncementAssetDetails={setAnnouncementAssetDetails}
            />
          </React.Fragment>
        ))}
        <View style={{ height: '80vh' }}></View>
      </List>
      {showGroupModal && (
        <Modal
          showModal={showGroupModal}
          setShowModal={setShowGroupModal}
          modalType="Choice"
        >
          <BidGroup
            selectedSaleAsset={showGroupModal}
            saleAssets={saleAssets}
            setShowModal={setShowGroupModal}
          />
        </Modal>
      )}
      {showBlastModal && (
        <Modal
          showModal={showBlastModal}
          setShowModal={setShowBlastModal}
          modalType={`${announcementAssetDetails || ''} Announcement`}
        >
          <AnnouncementInput
            saleAsset={showBlastModal}
            sale={sale}
            setShowModal={setShowBlastModal}
          />
        </Modal>
      )}
      {saleAssetAnnouncements &&
        <Modal
          showModal={saleAssetAnnouncements}
          setShowModal={setSaleAssetAnnouncements}
          modalType={`${announcementAssetDetails || ''} Announcements`}
        >
          <View>
            {
              saleAssetAnnouncements.map(announcement => {
                return <View style={{margin: '10px'}}>
                  {announcement.body}
                </View>
              })
            }
          </View>
        </Modal>
      }
      {sale.bidGroupChoiceId && (
        <Modal
          showModal
          modalType="Choice"
          setShowModal={false}
        >
          <BidGroupChoose
            sale={sale}
            saleAssets={saleAssets}
          />
        </Modal>
      )}
    </Container>
  );
};

export default SaleAssets;
